import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n";

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CalendarGrid = ({ blocked = [], interactive = false, onToggle, months = 2, testidPrefix = "calendar" }) => {
  const { t, lang } = useLanguage();
  const [offset, setOffset] = useState(0);
  const today = new Date();
  const todayISO = toISO(today);
  const blockedSet = new Set(blocked);

  const weekdays = [...Array(7)].map((_, i) =>
    new Intl.DateTimeFormat(lang, { weekday: "narrow" }).format(new Date(2026, 0, 5 + i))
  );

  const buildMonth = (off) => {
    const first = new Date(today.getFullYear(), today.getMonth() + off, 1);
    const y = first.getFullYear();
    const m = first.getMonth();
    const name = new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(first);
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const lead = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(toISO(new Date(y, m, d)));
    return { name, cells };
  };

  const monthList = [...Array(months)].map((_, i) => buildMonth(offset + i));

  return (
    <div data-testid={`${testidPrefix}-grid`}>
      <div className="flex items-center justify-end gap-2 mb-4">
        <button
          data-testid={`${testidPrefix}-prev`}
          onClick={() => setOffset(Math.max(0, offset - 1))}
          disabled={offset === 0}
          className="p-2 rounded-full border border-[#0D253A]/15 text-[#0D253A] hover:border-[#C86D51] hover:text-[#C86D51] disabled:opacity-30 transition-colors"
          aria-label={t.availability.prev}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          data-testid={`${testidPrefix}-next`}
          onClick={() => setOffset(offset + 1)}
          className="p-2 rounded-full border border-[#0D253A]/15 text-[#0D253A] hover:border-[#C86D51] hover:text-[#C86D51] transition-colors"
          aria-label={t.availability.next}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className={`grid grid-cols-1 ${months > 1 ? "sm:grid-cols-2" : ""} gap-6`}>
        {monthList.map((mo) => (
          <div key={mo.name} className="bg-white rounded-2xl border border-[#0D253A]/10 p-5 shadow-[0_10px_35px_-20px_rgba(13,37,58,0.3)]">
            <p className="font-display text-lg text-[#0D253A] capitalize mb-3">{mo.name}</p>
            <div className="grid grid-cols-7 gap-1 text-center">
              {weekdays.map((w, i) => (
                <span key={i} className="text-[10px] font-semibold uppercase text-[#5C6773] py-1">{w}</span>
              ))}
              {mo.cells.map((d, i) => {
                if (!d) return <span key={i} />;
                const isBlocked = blockedSet.has(d);
                const isPast = d < todayISO;
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`${testidPrefix}-day-${d}`}
                    disabled={!interactive || isPast}
                    onClick={() => interactive && !isPast && onToggle?.(d)}
                    className={`aspect-square flex items-center justify-center text-xs rounded-full transition-colors duration-200 ${
                      isBlocked
                        ? "bg-[#C86D51]/15 text-[#C86D51] line-through font-semibold"
                        : isPast
                        ? "text-[#5C6773]/35"
                        : `text-[#0D253A] ${interactive ? "hover:bg-[#1E5F74]/15 cursor-pointer" : "cursor-default"}`
                    } ${d === todayISO ? "ring-1 ring-[#0D253A]" : ""}`}
                  >
                    {Number(d.slice(8))}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarGrid;
